import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";
import type { SystemRole } from "../../auth/queries";

/** A sidebar entry: the server has already decided the reader sees it. */
export interface CustomPageLink {
  id: ID;
  slug: string;
  title: string;
}

/** A page as its reader gets it. */
export interface ShownCustomPage extends CustomPageLink {
  /** Markdown with raw HTML, rendered unsanitized. */
  body: string;
  /** False only ever reaches a manager previewing a draft. */
  published: boolean;
  /** The server writes an audit row for every fetch of the page (`useCustomPage`). */
  audit_views: boolean;
}

/** The manager's full view of a stránka. */
export interface CustomPage extends ShownCustomPage {
  sort_order: number;
  roles: SystemRole[];
  team_ids: ID[];
  federation_ids: ID[];
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface CustomPagePayload {
  title: string;
  slug: string;
  body: string;
  published: boolean;
  sort_order: number;
  audit_views: boolean;
  roles: SystemRole[];
  team_ids: ID[];
  federation_ids: ID[];
}

interface PagesResponse<T> {
  pages: T[];
}

/** The pages the signed-in user reads, targeting applied, in sidebar order. */
export function useVisibleCustomPages() {
  return useQuery<CustomPageLink[]>({
    queryKey: ["custom-pages", "mine"],
    queryFn: async () => (await apiGet<PagesResponse<CustomPageLink>>("/custom-pages")).pages,
  });
}

/**
 * One page to read; a 404 for anybody it was not written for. The server
 * records every fetch of a page with `audit_views`, so one fetch must be
 * one opening: nothing kept once the page is left (`gcTime: 0`, or a
 * reopened page would show its old body before, or without, the recorded
 * fetch), and never refetched behind the reader's back.
 */
export function useCustomPage(slug: string | undefined) {
  return useQuery<ShownCustomPage>({
    queryKey: ["custom-pages", "slug", slug],
    queryFn: () => apiGet<ShownCustomPage>(`/custom-pages/by-slug/${encodeURIComponent(slug ?? "")}`),
    enabled: !!slug,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Every page, bodies left out, for the admin list. */
export function useAllCustomPages() {
  return useQuery<CustomPage[]>({
    queryKey: ["custom-pages", "all"],
    queryFn: async () => (await apiGet<PagesResponse<CustomPage>>("/custom-pages/all")).pages,
  });
}

/** One page with its body, for the editor. */
export function useCustomPageForEdit(id: ID | undefined) {
  return useQuery<CustomPage>({
    queryKey: ["custom-pages", "edit", id],
    queryFn: () => apiGet<CustomPage>(`/custom-pages/${id}`),
    enabled: !!id,
    // The editor hydrates its form once; a refetch must not reset typing.
    refetchOnWindowFocus: false,
  });
}

export function useSaveCustomPage(id: ID | null) {
  const qc = useQueryClient();
  return useMutation<CustomPage, ApiError, CustomPagePayload>({
    mutationFn: (payload) =>
      id
        ? apiPut<CustomPage>(`/custom-pages/${id}`, payload)
        : apiPost<CustomPage>("/custom-pages", payload),
    // The editor renders the refusal beside its form.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-pages"] });
    },
  });
}

export function useDeleteCustomPage() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ID>({
    mutationFn: (id) => apiDelete<void>(`/custom-pages/${id}`),
    // Reported inside the confirm dialog.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-pages"] });
    },
  });
}
